import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../../components/ui/command';
import {
  Clock, Plus, Trash2, Loader2, ChevronLeft, ChevronRight, CheckCircle, AlertCircle, Save, MessageSquare, ChevronsUpDown, Search, Users, User
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const DAILY_TARGET = 480;

const SOURCE_LABELS = { assigned: 'Assigned to me', collaboration: 'Collaboration', created: 'Created by me' };
const SOURCE_ICONS = { assigned: User, collaboration: Users, created: User };

const TaskCombobox = ({ tasks, value, displayValue, onSelect, onCustom, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const grouped = useMemo(() => {
    const groups = {};
    for (const t of tasks) {
      const src = t._source || 'assigned';
      if (!groups[src]) groups[src] = [];
      groups[src].push(t);
    }
    return groups;
  }, [tasks]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
          className="w-full justify-between text-sm font-normal h-9 px-3" data-testid={testId}>
          <span className="truncate">{displayValue || 'Select task...'}</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search tasks..." value={search} onValueChange={setSearch} />
          <CommandList className="max-h-[200px]">
            <CommandEmpty>No tasks found.</CommandEmpty>
            {Object.entries(grouped).map(([src, srcTasks]) => {
              const filtered = search
                ? srcTasks.filter(t => t.title.toLowerCase().includes(search.toLowerCase()))
                : srcTasks;
              if (filtered.length === 0) return null;
              const Icon = SOURCE_ICONS[src] || User;
              return (
                <CommandGroup key={src} heading={
                  <span className="flex items-center gap-1.5 text-[11px]">
                    <Icon className="h-3 w-3" />{SOURCE_LABELS[src] || src}
                  </span>
                }>
                  {filtered.map(t => (
                    <CommandItem key={t.id} value={t.id}
                      onSelect={() => { onSelect(t.id, t.title); setOpen(false); setSearch(''); }}
                      className="flex items-center justify-between gap-2">
                      <span className="truncate flex-1">{t.title}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        {t.priority === 'High' && <Badge variant="outline" className="text-[9px] px-1 py-0 border-red-300 text-red-500">High</Badge>}
                        {t.priority === 'Urgent' && <Badge variant="outline" className="text-[9px] px-1 py-0 border-red-500 text-red-600">Urgent</Badge>}
                        {value === t.id && <CheckCircle className="h-3.5 w-3.5 text-blue-600" />}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
            <CommandGroup>
              <CommandItem onSelect={() => { onCustom(); setOpen(false); setSearch(''); }}
                className="text-gray-500 italic">
                <Plus className="h-3.5 w-3.5 mr-1" /> Custom entry...
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const MyTimesheet = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [timesheet, setTimesheet] = useState(null);
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weekTimesheets, setWeekTimesheets] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);

  const [submitting, setSubmitting] = useState(false);

  const { getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchCategories(); fetchAvailableTasks(); }, [currentOffice]);
  useEffect(() => { fetchTimesheet(); fetchWeekSummary(); }, [selectedDate, currentOffice]);

  const fetchCategories = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/timesheets/categories`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCategories(data.filter(c => c.is_active));
      }
    } catch (err) { console.error(err); }
  };

  const fetchAvailableTasks = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const [myRes, collabRes, createdRes] = await Promise.all([
        fetch(`${API_URL}/api/tasks/my-tasks`, { headers }),
        fetch(`${API_URL}/api/tasks/collaborations`, { headers }),
        fetch(`${API_URL}/api/tasks/created-by-me`, { headers }),
      ]);
      const seen = new Set();
      const all = [];
      const addTasks = (tasks, source) => {
        for (const t of tasks) {
          if (!seen.has(t.id) && t.status !== 'Completed' && t.status !== 'Cancelled') {
            seen.add(t.id);
            all.push({ ...t, _source: source });
          }
        }
      };
      if (myRes.ok) addTasks(await myRes.json(), 'assigned');
      if (collabRes.ok) addTasks(await collabRes.json(), 'collaboration');
      if (createdRes.ok) addTasks(await createdRes.json(), 'created');
      setAvailableTasks(all);
    } catch (err) { console.error(err); }
  };

  const fetchTimesheet = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/timesheets/my/${selectedDate}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTimesheet(data);
        setEntries(data.entries?.length > 0 ? data.entries : [{ id: 'new-1', task: '', task_id: '', category: '', project: '', description: '', minutes: 0 }]);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchWeekSummary = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const d = new Date(selectedDate);
      const day = d.getDay();
      const monday = new Date(d);
      monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const start = monday.toISOString().split('T')[0];
      const end = sunday.toISOString().split('T')[0];
      const res = await fetch(`${API_URL}/api/timesheets/my?start_date=${start}&end_date=${end}`, { headers });
      if (res.ok) setWeekTimesheets(await res.json());
    } catch (err) { console.error(err); }
  };

  const totalMinutes = useMemo(() => entries.reduce((sum, e) => sum + (parseInt(e.minutes) || 0), 0), [entries]);
  const progressPct = Math.min((totalMinutes / DAILY_TARGET) * 100, 100);

  const addEntry = () => {
    setEntries([...entries, { id: `new-${Date.now()}`, task: '', task_id: '', category: '', project: '', description: '', minutes: 0 }]);
  };

  const removeEntry = (idx) => {
    if (entries.length <= 1) return;
    setEntries(entries.filter((_, i) => i !== idx));
  };

  const updateEntry = (idx, field, value) => {
    const updated = [...entries];
    updated[idx] = { ...updated[idx], [field]: field === 'minutes' ? parseInt(value) || 0 : value };
    setEntries(updated);
  };

  const handleSave = async (asDraft = false) => {
    // For a draft save, keep partial rows (task or minutes provided). For a
    // regular submit ("Log Time"), require all three: task, category, minutes.
    const validEntries = entries
      .filter(e => {
        if (asDraft) return (e.task && e.task.trim()) || e.minutes > 0;
        return e.task && e.category && e.minutes > 0;
      })
      .map(e => ({
        task: e.task || '',
        task_id: e.task_id || undefined,
        category: e.category || 'Core Work',
        minutes: e.minutes || 0,
        description: e.description || undefined,
      }));
    if (validEntries.length === 0) {
      toast.error(asDraft ? 'Nothing to save — add at least one row with content' : 'Add at least one complete entry');
      return;
    }
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const body = { date: selectedDate, entries: validEntries };
      if (asDraft) body.source = 'day_planner'; // keeps status as Draft on the backend
      const res = await fetch(`${API_URL}/api/timesheets`, {
        method: 'POST', headers, body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.json();
        setTimesheet(data);
        setEntries(data.entries);
        toast.success(asDraft ? 'Draft saved' : 'Time logged successfully');
        fetchWeekSummary();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleSubmitTimesheet = async () => {
    if (!timesheet?.id) return;
    setSubmitting(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/timesheets/${timesheet.id}/submit`, {
        method: 'PUT', headers,
      });
      if (res.ok) {
        const data = await res.json();
        setTimesheet(data);
        toast.success('Timesheet submitted! Now visible to your manager.');
        fetchWeekSummary();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit');
      }
    } catch (err) { toast.error('Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const navigateDay = (offset) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const formatMinutes = (m) => `${Math.floor(m / 60)}h ${m % 60}m`;
  const dayName = new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  // Editable only for today or yesterday
  const todayStr = new Date().toISOString().split('T')[0];
  const yest = new Date(); yest.setDate(yest.getDate() - 1);
  const yesterdayStr = yest.toISOString().split('T')[0];
  const isEditable = selectedDate === todayStr || selectedDate === yesterdayStr;

  // Week day cells
  const weekDays = useMemo(() => {
    const d = new Date(selectedDate);
    const day = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    return Array.from({ length: 7 }, (_, i) => {
      const dd = new Date(monday);
      dd.setDate(monday.getDate() + i);
      const ds = dd.toISOString().split('T')[0];
      const ts = weekTimesheets.find(t => t.date === ds);
      return { date: ds, label: dd.toLocaleDateString('en-US', { weekday: 'short' }), day: dd.getDate(), ts };
    });
  }, [selectedDate, weekTimesheets]);

  // Weekly total
  const weeklyTotal = useMemo(() => weekTimesheets.reduce((s, t) => s + (t.total_minutes || 0), 0), [weekTimesheets]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="my-timesheet-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Timesheet</h1>
        <p className="text-gray-500">Log your daily work hours. Target: 8h/day, 40h/week.</p>
      </div>

      {/* Week Overview */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-gray-600">This Week</p>
            <p className="text-sm font-semibold text-gray-800">{formatMinutes(weeklyTotal)} / 40h 0m</p>
          </div>
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map(wd => {
              const isSelected = wd.date === selectedDate;
              const isToday = wd.date === new Date().toISOString().split('T')[0];
              const mins = wd.ts?.total_minutes || 0;
              const pct = Math.min((mins / DAILY_TARGET) * 100, 100);
              const hasComments = wd.ts?.manager_comments?.length > 0;
              const isDraft = wd.ts?.status === 'Draft';
              return (
                <div key={wd.date} onClick={() => setSelectedDate(wd.date)}
                  className={`p-2.5 rounded-lg cursor-pointer text-center transition-all border-2 ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-transparent hover:bg-gray-50'}`}
                  data-testid={`week-day-${wd.date}`}>
                  <div className={`text-[11px] font-medium ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>{wd.label}</div>
                  <div className={`text-lg font-bold ${isSelected ? 'text-blue-700' : ''}`}>{wd.day}</div>
                  {mins > 0 && (
                    <>
                      <div className="h-1 bg-gray-200 rounded-full mt-1 overflow-hidden">
                        <div className={`h-full rounded-full ${isDraft ? 'bg-amber-400' : pct >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-[10px] text-gray-500 mt-0.5">{formatMinutes(mins)}</p>
                    </>
                  )}
                  {isDraft && mins > 0 && <p className="text-[9px] text-amber-600 font-medium">Draft</p>}
                  {hasComments && <MessageSquare className="h-3 w-3 text-amber-500 mx-auto mt-0.5" />}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Day Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigateDay(-1)} data-testid="prev-day"><ChevronLeft className="h-4 w-4" /></Button>
          <div>
            <h2 className="text-lg font-semibold">{dayName}</h2>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="h-3.5 w-3.5" />
              <span>{formatMinutes(totalMinutes)} / {formatMinutes(DAILY_TARGET)}</span>
            </div>
          </div>
          <Button variant="outline" size="icon" onClick={() => navigateDay(1)} data-testid="next-day"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="flex items-center gap-2">
          {timesheet?.status === 'Draft' && (
            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">Draft</Badge>
          )}
          {timesheet?.status === 'Logged' && (
            <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">Submitted</Badge>
          )}
          {timesheet?.status === 'Draft' && timesheet?.id && (
            <Button onClick={handleSubmitTimesheet} disabled={submitting} variant="default" className="bg-green-600 hover:bg-green-700" data-testid="submit-timesheet">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />} Submit to Manager
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleSave(true)}
            disabled={saving || !isEditable}
            data-testid="save-timesheet-draft"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Draft
          </Button>
          <Button onClick={() => handleSave(false)} disabled={saving || !isEditable} data-testid="save-timesheet">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Log Time
          </Button>
        </div>
      </div>

      {/* Not editable notice */}
      {!isEditable && (
        <div className="text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" /> You can only log time for today or yesterday.
        </div>
      )}

      {/* Progress Bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${progressPct >= 100 ? 'bg-green-500' : progressPct >= 50 ? 'bg-blue-500' : 'bg-orange-400'}`}
          style={{ width: `${progressPct}%` }} />
      </div>

      {/* Manager Comments */}
      {timesheet?.manager_comments?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-3">
            <p className="text-sm font-medium text-amber-800 flex items-center gap-1.5 mb-2">
              <MessageSquare className="h-4 w-4" /> Manager Notes
            </p>
            <div className="space-y-1.5">
              {timesheet.manager_comments.map((c, i) => (
                <div key={c.id || i} className="text-sm">
                  <span className="font-medium text-gray-700">{c.by_name}:</span>{' '}
                  <span className="text-gray-600">{c.comment}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entries */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Time Entries</CardTitle>
            {isEditable && (
              <Button variant="outline" size="sm" onClick={addEntry} data-testid="add-entry"><Plus className="h-4 w-4 mr-1" />Add Entry</Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <div className="text-center py-8 text-gray-500"><Clock className="h-8 w-8 mx-auto mb-2 opacity-50" /><p>No entries yet</p></div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide px-1">
                <div className="col-span-3">Task</div>
                <div className="col-span-3">Description</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-2">Time</div>
                <div className="col-span-2"></div>
              </div>
              {entries.map((entry, idx) => (
                <div key={entry.id || idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`entry-row-${idx}`}>
                  <div className="col-span-3">
                    {isEditable ? (
                      <>
                        <TaskCombobox
                          tasks={availableTasks}
                          value={entry.task_id}
                          displayValue={entry.task}
                          onSelect={(taskId, taskTitle) => {
                            const updated = [...entries];
                            updated[idx] = { ...updated[idx], task: taskTitle, task_id: taskId };
                            setEntries(updated);
                          }}
                          onCustom={() => {
                            const updated = [...entries];
                            updated[idx] = { ...updated[idx], task: '', task_id: '' };
                            setEntries(updated);
                          }}
                          testId={`entry-task-${idx}`}
                        />
                        {!entry.task_id && (
                          <Input value={entry.task} onChange={e => updateEntry(idx, 'task', e.target.value)}
                            placeholder="Type custom task" className="text-sm mt-1" data-testid={`entry-task-custom-${idx}`} />
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-gray-700 px-3 py-2 truncate" title={entry.task}>{entry.task}</div>
                    )}
                  </div>
                  <div className="col-span-3">
                    <Input value={entry.description || ''} onChange={e => updateEntry(idx, 'description', e.target.value)}
                      placeholder="What did you do?" disabled={!isEditable} className="text-sm" data-testid={`entry-description-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <Select value={entry.category} onValueChange={v => updateEntry(idx, 'category', v)} disabled={!isEditable}>
                      <SelectTrigger className="text-sm" data-testid={`entry-category-${idx}`}><SelectValue placeholder="Category" /></SelectTrigger>
                      <SelectContent>
                        {categories.map(c => (
                          <SelectItem key={c.id} value={c.name}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
                              {c.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center gap-1">
                      <Input type="number" min="0" max="720" value={entry.minutes || ''}
                        onChange={e => updateEntry(idx, 'minutes', e.target.value)}
                        disabled={!isEditable} className="text-sm" data-testid={`entry-minutes-${idx}`} />
                      <span className="text-xs text-gray-400 shrink-0">min</span>
                    </div>
                  </div>
                  <div className="col-span-2 flex justify-end items-center gap-1">
                    {entry.minutes > 0 && <span className="text-xs text-gray-500">{formatMinutes(entry.minutes)}</span>}
                    {entry.adjusted_by && (
                      <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-600">Adjusted</Badge>
                    )}
                    {entries.length > 1 && isEditable && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600"
                        onClick={() => removeEntry(idx)} data-testid={`remove-entry-${idx}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-12 gap-2 items-center pt-3 border-t">
                <div className="col-span-8 text-right font-semibold text-sm text-gray-700">Total</div>
                <div className="col-span-2">
                  <span className={`font-bold text-sm ${totalMinutes >= DAILY_TARGET ? 'text-green-600' : totalMinutes > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                    {formatMinutes(totalMinutes)}
                  </span>
                </div>
                <div className="col-span-2 text-right">
                  {totalMinutes < DAILY_TARGET && totalMinutes > 0 && (
                    <span className="text-xs text-orange-500 flex items-center gap-1 justify-end">
                      <AlertCircle className="h-3 w-3" /> {formatMinutes(DAILY_TARGET - totalMinutes)} left
                    </span>
                  )}
                  {totalMinutes >= DAILY_TARGET && (
                    <span className="text-xs text-green-600 flex items-center gap-1 justify-end">
                      <CheckCircle className="h-3 w-3" /> Target met
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyTimesheet;
